// src/pages/PerformanceAssessmentWeights/index.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormInput,
    CFormSelect,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';
import { ROLE_LABELS } from '../performanceAssessments/questions';

// All possible fields that can have weights
const WEIGHT_FIELDS = [
    'innovation',
    'quality',
    'commitment',
    'client_orientation',
    'team_management',
    'planning',
    'results',
    'rigor',
    'autonomy',
    'organization',
    'teamwork',
    'project_management',
    'project_execution',
    'it',
    'technical_capacity',
    'sales',
    'knowledge',
];

// C1–C4 groups (for header & colouring)
const WEIGHT_GROUPS = [
    {
        key: 'c1',
        label: 'Organizacionais',
        fields: ['innovation', 'quality', 'commitment', 'client_orientation'],
    },
    {
        key: 'c2',
        label: 'Liderança',
        fields: ['team_management', 'planning'],
    },
    {
        key: 'c3',
        label: 'Comportamentais',
        fields: ['autonomy', 'organization', 'results', 'rigor', 'teamwork'],
    },
    {
        key: 'c4',
        label: 'Competências Técnicas',
        fields: ['project_management', 'project_execution', 'it', 'technical_capacity', 'sales', 'knowledge'],
    },
];

const fieldLabel = (key) => {
    switch (key) {
        case 'innovation':
            return 'Inovação';
        case 'quality':
            return 'Qualidade';
        case 'commitment':
            return 'Compromisso';
        case 'client_orientation':
            return 'Orientação para o cliente';
        case 'team_management':
            return 'Gestão de equipas';
        case 'planning':
            return 'Planeamento';
        case 'results':
            return 'Orientação para os resultados';
        case 'rigor':
            return 'Rigor';
        case 'autonomy':
            return 'Autonomia';
        case 'organization':
            return 'Capacidade de organização';
        case 'teamwork':
            return 'Trabalho em Equipa';
        case 'project_management':
            return 'Gestão de Projetos';
        case 'project_execution':
            return 'Execução de Projetos';
        case 'it':
            return 'Capacidade de Utilização TIC';
        case 'technical_capacity':
            return 'Capacidade Técnica';
        case 'sales':
            return 'Vendas';
        case 'knowledge':
            return 'Conhecimento, Informação das Normas';
        default:
            return key;
    }
};

// Roles list (keeps order stable)
const ROLE_KEYS = Object.keys(ROLE_LABELS);

// Helper to build an empty row for a role/year
const buildEmptyRow = (year, role) => ({
    id: null,
    year,
    role,
    ...Object.fromEntries(WEIGHT_FIELDS.map((f) => [f, ''])),
});

const PerformanceAssessmentWeightsPage = () => {
    const currentYear = new Date().getFullYear();

    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [allWeights, setAllWeights] = useState([]); // raw from API
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [rowsByRole, setRowsByRole] = useState({}); // { role: { ...weightsRow } }

    const [availableYears, setAvailableYears] = useState([currentYear]);

    // ---- Fetch all records once ----
    const fetchWeights = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/performance-assessment-weights/');
            const data = response.data || [];
            setAllWeights(data);

            const yearsSet = new Set(data.map((w) => w.year).filter(Boolean));
            if (!yearsSet.size) {
                yearsSet.add(currentYear);
            }
            const yearsArr = Array.from(yearsSet).sort((a, b) => b - a);
            setAvailableYears(yearsArr);

            // normalise selectedYear if needed
            if (!yearsArr.includes(selectedYear)) {
                setSelectedYear(yearsArr[0]);
            }
        } catch (error) {
            console.error('Error fetching performance assessment weights:', error);
            setToast({
                title: 'Ponderações Avaliação de Desempenho',
                message: 'Erro ao carregar configurações de ponderações.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [currentYear, selectedYear]);

    useEffect(() => {
        fetchWeights();
    }, [fetchWeights]);

    // ---- Build editable rows for the selected year ----
    useEffect(() => {
        if (!allWeights.length) {
            // no data yet – build empty rows for all roles
            const initial = {};
            ROLE_KEYS.forEach((role) => {
                initial[role] = buildEmptyRow(selectedYear, role);
            });
            setRowsByRole(initial);
            return;
        }

        const rows = {};
        ROLE_KEYS.forEach((role) => {
            const existing = allWeights.find(
                (w) => w.year === selectedYear && w.role === role
            );
            if (existing) {
                const row = {
                    id: existing.id,
                    year: existing.year,
                    role: existing.role,
                };
                WEIGHT_FIELDS.forEach((f) => {
                    // convert null to '' for editing
                    row[f] =
                        existing[f] === null || existing[f] === undefined
                            ? ''
                            : String(existing[f]);
                });
                rows[role] = row;
            } else {
                rows[role] = buildEmptyRow(selectedYear, role);
            }
        });

        setRowsByRole(rows);
    }, [allWeights, selectedYear]);

    const handleYearChange = (e) => {
        const newYear = parseInt(e.target.value, 10);
        if (!Number.isNaN(newYear)) {
            setSelectedYear(newYear);
        }
    };

    // Calculate sum for a given "row object"
    const calcRowSum = (row) =>
        WEIGHT_FIELDS.reduce((acc, f) => {
            const v = row[f];
            const num = v === '' || v === null || v === undefined ? 0 : Number(v);
            return acc + (Number.isNaN(num) ? 0 : num);
        }, 0);

    const handleChangeField = (roleKey, field) => (e) => {
        const rawValue = e.target.value;

        if (rawValue === '') {
            setRowsByRole((prev) => ({
                ...prev,
                [roleKey]: {
                    ...(prev[roleKey] || buildEmptyRow(selectedYear, roleKey)),
                    [field]: '',
                },
            }));
            return;
        }

        const numeric = parseInt(rawValue, 10);
        if (Number.isNaN(numeric)) {
            return;
        }

        setRowsByRole((prev) => {
            const currentRow = prev[roleKey] || buildEmptyRow(selectedYear, roleKey);
            const candidateRow = {
                ...currentRow,
                [field]: numeric,
            };

            const sum = calcRowSum(candidateRow);
            if (sum > 100) {
                setToast({
                    title: 'Ponderações Avaliação de Desempenho',
                    message: `A soma das ponderações para "${ROLE_LABELS[roleKey] || roleKey}" não pode ultrapassar 100% (atual tentativa: ${sum.toFixed(
                        0
                    )}%).`,
                    type: 'error',
                });
                return prev;
            }

            return {
                ...prev,
                [roleKey]: candidateRow,
            };
        });
    };


    // ---- Save: loop roles & call API per record ----
    const handleSaveAll = async () => {
        setSaving(true);
        try {
            const requests = [];

            Object.values(rowsByRole).forEach((row) => {
                // Skip rows fully empty & without id
                const hasAnyValue = WEIGHT_FIELDS.some(
                    (f) => row[f] !== '' && row[f] !== null && row[f] !== undefined
                );
                if (!hasAnyValue && !row.id) {
                    return;
                }

                const payload = {
                    year: Number(row.year),
                    role: String(row.role).toLocaleLowerCase(),
                };

                WEIGHT_FIELDS.forEach((f) => {
                    const raw = row[f];
                    if (raw === '' || raw === null || raw === undefined) {
                        payload[f] = null;
                    } else {
                        payload[f] = parseInt(raw, 10);
                    }
                });

                // Double-check sum on save (defensive)
                const sum = WEIGHT_FIELDS.reduce((acc, f) => {
                    const v = payload[f];
                    const num = v === null || v === undefined ? 0 : v;
                    return acc + (Number.isNaN(num) ? 0 : num);
                }, 0);

                if (sum > 100) {
                    // skip this row, warn
                    return;
                }

                if (row.id) {
                    requests.push(
                        apiClient.put(`/performance-assessment-weights/${row.id}/`, payload)
                    );
                } else {
                    requests.push(apiClient.post('/performance-assessment-weights/', payload));
                }
            });

            if (requests.length) {
                await Promise.all(requests);
            }

            setToast({
                title: 'Ponderações Avaliação de Desempenho',
                message: 'Configuração de ponderações guardada com sucesso para o ano seleccionado.',
                type: 'success',
            });

            // re-fetch to refresh ids, etc.
            await fetchWeights();
        } catch (error) {
            console.error('Error saving performance assessment weights:', error);
            setToast({
                title: 'Ponderações Avaliação de Desempenho',
                message: 'Erro ao guardar a configuração de ponderações.',
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const rowsArray = useMemo(
        () => ROLE_KEYS.map((role) => rowsByRole[role] || buildEmptyRow(selectedYear, role)),
        [rowsByRole, selectedYear]
    );

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <div className="d-flex justify-content-end mb-3">
                <CButton
                    color="info"
                    className="text-white"
                    disabled={loading || saving}
                    onClick={async () => {
                        try {
                            setSaving(true);
                            await apiClient.post('/performance-assessment-weights/generate/', {
                                year: selectedYear, // ou new Date().getFullYear()
                            });
                            await fetchWeights();
                            setToast({
                                title: 'Ponderações Avaliação de Desempenho',
                                message: 'Registos gerados com sucesso',
                                type: 'success',
                            });
                        } catch (e) {
                            setToast({
                                title: 'Ponderações Avaliação de Desempenho',
                                message: 'Erro ao gerar registos do ano.',
                                type: 'error',
                            });
                        } finally {
                            setSaving(false);
                        }
                    }}
                >
                    Gerar novas ponderações
                </CButton>
            </div>

            <CCard className="mb-4">
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="mb-0">Configuração de Ponderações – Avaliação de Desempenho</h3>
                        <small className="text-muted">
                            Edite as ponderações de todas as funções para um ano em simultâneo. A soma de cada linha
                            não pode exceder 100%.
                        </small>
                    </div>
                    <CRow className="g-2 align-items-center">
                        <CCol xs="auto">
                            <strong>Ano:&nbsp;</strong>
                        </CCol>
                        <CCol xs="auto">
                            <CFormSelect
                                size="sm"
                                value={selectedYear}
                                onChange={handleYearChange}
                            >
                                {/* allow “ad-hoc” year even if not in list */}
                                {!availableYears.includes(selectedYear) && (
                                    <option value={selectedYear}>{selectedYear}</option>
                                )}
                                {availableYears.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>
                    </CRow>
                </CCardHeader>
                <CCardBody>
                    {loading ? (
                        <div className="text-center my-3">
                            <CSpinner size="sm" /> A carregar…
                        </div>
                    ) : (
                        <CForm
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSaveAll();
                            }}
                        >
                            <div className="table-responsive">
                                <CTable bordered small align="middle">
                                    <CTableHead>
                                        {/* Group header row with colSpan & colours */}
                                        <CTableRow>
                                            <CTableHeaderCell rowSpan={2} style={{ minWidth: 180 }}>
                                                Função
                                            </CTableHeaderCell>
                                            {WEIGHT_GROUPS.map((group, index) => (
                                                <CTableHeaderCell
                                                    key={group.key}
                                                    className="text-center"
                                                    colSpan={group.fields.length}
                                                    style={{
                                                        backgroundColor:
                                                            index % 2 === 0 ? '#fff9c4' : '#e3f2fd', // yellow / blue
                                                        fontWeight: '600',
                                                    }}
                                                >
                                                    {group.label}
                                                </CTableHeaderCell>
                                            ))}
                                        </CTableRow>
                                        {/* Second header row with each field label */}
                                        <CTableRow>
                                            {WEIGHT_GROUPS.map((group, index) =>
                                                group.fields.map((field) => (
                                                    <CTableHeaderCell
                                                        key={field}
                                                        className="text-center"
                                                        style={{
                                                            backgroundColor:
                                                                index % 2 === 0 ? '#fff9c4' : '#e3f2fd',
                                                        }}
                                                    >
                                                        {fieldLabel(field)}
                                                    </CTableHeaderCell>
                                                ))
                                            )}
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {rowsArray.map((row) => {
                                            const total = calcRowSum(row);
                                            return (
                                                <CTableRow key={row.role}>
                                                    <CTableDataCell className="fw-semibold">
                                                        {ROLE_LABELS[row.role] || row.role}
                                                        <div className="small text-muted">
                                                            Total:&nbsp;{total.toFixed(2)}%
                                                        </div>
                                                    </CTableDataCell>

                                                    {WEIGHT_GROUPS.map((group, index) =>
                                                        group.fields.map((field) => (
                                                            <CTableDataCell
                                                                key={`${row.role}-${field}`}
                                                                style={{
                                                                    minWidth: 130,
                                                                    backgroundColor:
                                                                        index % 2 === 0 ? '#fffdf3' : '#f5f9ff',
                                                                }}
                                                            >
                                                                <CFormInput
                                                                    type="number"
                                                                    step="1"
                                                                    min="0"
                                                                    max="100"
                                                                    value={row[field]}
                                                                    onChange={handleChangeField(row.role, field)}
                                                                />
                                                            </CTableDataCell>
                                                        ))
                                                    )}
                                                </CTableRow>
                                            );
                                        })}
                                    </CTableBody>
                                </CTable>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <CButton
                                    color="success"
                                    type="submit"
                                    disabled={saving}
                                >
                                    {saving ? <CSpinner size="sm" /> : 'Guardar Ponderações do Ano'}
                                </CButton>
                            </div>
                        </CForm>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
};

export default PerformanceAssessmentWeightsPage;
